import {
  Box,
  Paper,
  Typography,
  Chip,
  Skeleton,
  Divider,
  IconButton,
} from "@mui/material";
import {
  Person,
  Phone,
  Email,
  LocationOn,
  ArrowBack,
  Assignment,
  CalendarToday,
  Architecture,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import theme from "../../theme/theme";
import { fetchCustomerById } from "../../api/CustomerService";

const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="500">
        {value || "-"}
      </Typography>
    </Box>
  </Box>
);

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [fetchingCustomer, setFetchingCustomer] = useState(true);

  const fetchCustomer = async () => {
    setFetchingCustomer(true);
    try {
      const response = await fetchCustomerById(id);
      setCustomer(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch customer details");
    } finally {
      setFetchingCustomer(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const CardSkeleton = () => (
    <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius, mb: 2 }}>
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      {[...Array(4)].map((_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
        >
          <Skeleton variant="circular" width={24} height={24} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={180} height={20} />
          </Box>
        </Box>
      ))}
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <IconButton
          onClick={() => navigate("/customers")}
          sx={{
            backgroundColor: theme.palette.primary.main + "12",
            "&:hover": { backgroundColor: theme.palette.primary.main + "25" },
          }}
        >
          <ArrowBack sx={{ color: theme.palette.primary.main }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="700">
            {fetchingCustomer ? (
              <Skeleton width={200} />
            ) : (
              customer?.name || "Customer Detail"
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {fetchingCustomer ? (
              <Skeleton width={120} />
            ) : (
              `Customer #${customer?.id || ""}`
            )}
          </Typography>
        </Box>
        {!fetchingCustomer && customer && (
          <Chip
            label={customer.is_active ? "Active" : "Inactive"}
            color={customer.is_active ? "success" : "default"}
            variant="outlined"
            sx={{ fontWeight: 600, px: 1 }}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2.5 }}>
        {/* Left Column — 40% (Customer Info) */}
        <Box sx={{ flex: "0 0 40%", minWidth: 0 }}>
          {/* Client Details Card */}
          {fetchingCustomer ? (
            <CardSkeleton />
          ) : (
            <Paper
              sx={{
                p: 3,
                borderRadius: theme.paper.borderRadius,
                backgroundColor: theme.paper.backgroundColor,
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color={theme.palette.primary.main}
                sx={{ mb: 2 }}
              >
                Customer Details
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1,
                }}
              >
                <InfoRow
                  icon={<Person fontSize="small" />}
                  label="Name"
                  value={customer?.name}
                />
                <Divider />
                <InfoRow
                  icon={<Email fontSize="small" />}
                  label="Email"
                  value={customer?.email}
                />
                <Divider />
                <InfoRow
                  icon={<Phone fontSize="small" />}
                  label="Phone"
                  value={customer?.phone}
                />
                <Divider />
                <InfoRow
                  icon={<LocationOn fontSize="small" />}
                  label="Address"
                  value={customer?.address}
                />
              </Box>
            </Paper>
          )}
        </Box>

        {/* Right Column — 60% (Associated Leads + Projects) */}
        <Box sx={{ flex: "0 0 60%", minWidth: 0 }}>
          {/* Associated Leads */}
          <Paper
            sx={{
              p: 3,
              borderRadius: theme.paper.borderRadius,
              backgroundColor: theme.paper.backgroundColor,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color={theme.palette.primary.main}
              >
                Associated Leads
              </Typography>
              {!fetchingCustomer && customer?.leads && (
                <Chip
                  label={customer.leads.length}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: theme.palette.primary.main + "18",
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            </Box>

            {fetchingCustomer ? (
              <Box>
                {[...Array(2)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={80}
                    sx={{ mb: 1.5 }}
                  />
                ))}
              </Box>
            ) : customer?.leads?.length > 0 ? (
              customer.leads.map((lead) => (
                <Box
                  key={lead.id}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.primary.main + "06",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Assignment
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body2" fontWeight="600">
                        Lead #{lead.id}
                      </Typography>
                    </Box>
                    <Chip
                      label={lead.status}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, height: 24, fontSize: 11 }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {lead.lift_type && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Type:</strong> {lead.lift_type}
                      </Typography>
                    )}
                    {lead.capacity_persons && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Capacity:</strong> {lead.capacity_persons}{" "}
                        persons
                        {lead.capacity_kg ? ` / ${lead.capacity_kg} kg` : ""}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 1,
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: 12, color: "text.disabled" }}
                    />
                    <Typography variant="caption" color="text.disabled">
                      Created on: {formatDate(lead.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Assignment
                  sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No leads associated with this customer
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Associated Projects */}
          <Paper
            sx={{
              p: 3,
              borderRadius: theme.paper.borderRadius,
              backgroundColor: theme.paper.backgroundColor,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color={theme.palette.primary.main}
              >
                Associated Projects
              </Typography>
              {!fetchingCustomer && customer?.projects && (
                <Chip
                  label={customer.projects.length}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: theme.palette.primary.main + "18",
                    color: theme.palette.primary.main,
                  }}
                />
              )}
            </Box>

            {fetchingCustomer ? (
              <Box>
                {[...Array(2)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={80}
                    sx={{ mb: 1.5 }}
                  />
                ))}
              </Box>
            ) : customer?.projects?.length > 0 ? (
              customer.projects.map((project) => (
                <Box
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: theme.palette.primary.main + "06",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Architecture
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body2" fontWeight="600">
                        Project #{project.project_id || project.id}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        project.status
                          ? project.status
                              .split("_")
                              .map(
                                (w) => w.charAt(0).toUpperCase() + w.slice(1),
                              )
                              .join(" ")
                          : "-"
                      }
                      size="small"
                      variant="outlined"
                      color={
                        project.status === "handed_over"
                          ? "success"
                          : project.status === "under_construction"
                            ? "warning"
                            : "info"
                      }
                      sx={{ fontWeight: 600, height: 24, fontSize: 11 }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {project.lift_type && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Type:</strong>{" "}
                        {project.lift_type
                          .split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ")}
                      </Typography>
                    )}
                    {project.capacity_persons && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Capacity:</strong> {project.capacity_persons}{" "}
                        persons
                        {project.capacity_kg
                          ? ` / ${project.capacity_kg} kg`
                          : ""}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 1,
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: 12, color: "text.disabled" }}
                    />
                    <Typography variant="caption" color="text.disabled">
                      Created on: {formatDate(project.created_at)}
                    </Typography>
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Architecture
                  sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No projects associated with this customer
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerDetailPage;
